// Mirrors flock-be auth.ValidatePasswordStrength (+ bcrypt's 72-byte input limit) so the
// change-password form rejects what the API would reject before a round trip. The API stays
// the source of truth: its messages are shown if the rules ever drift.

export const PASSWORD_MAX_BYTES = 72;

export type PasswordRule = { id: string; label: string; test: (pw: string) => boolean };

// Go's checks use len(password), i.e. UTF-8 bytes, for both the minimum and the maximum.
const utf8ByteLength = (s: string): number => {
  let bytes = 0;
  for (const ch of s) {
    const cp = ch.codePointAt(0) ?? 0;
    bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
  }
  return bytes;
};

export const PASSWORD_RULES: PasswordRule[] = [
  { id: 'length', label: 'At least 8 characters (accented ones count double)', test: (pw) => utf8ByteLength(pw) >= 8 },
  { id: 'upper', label: 'An uppercase letter', test: (pw) => /\p{Lu}/u.test(pw) },
  { id: 'lower', label: 'A lowercase letter', test: (pw) => /\p{Ll}/u.test(pw) },
  { id: 'number', label: 'A number', test: (pw) => /\p{Nd}/u.test(pw) },
  { id: 'special', label: 'A special character (e.g. ! # - _)', test: (pw) => /[\p{P}\p{S}]/u.test(pw) },
  { id: 'nospace', label: 'No spaces', test: (pw) => !/\s/u.test(pw) },
  {
    id: 'maxbytes',
    label: `At most ${PASSWORD_MAX_BYTES} bytes`,
    test: (pw) => utf8ByteLength(pw) <= PASSWORD_MAX_BYTES,
  },
];

/** Labels of the rules `pw` fails, in display order. */
export const unmetPasswordRules = (pw: string): string[] =>
  PASSWORD_RULES.filter((r) => !r.test(pw)).map((r) => r.label);

/** One-line message for a password that fails the policy, or '' when it passes. */
export const formatUnmetPasswordRules = (pw: string): string => {
  const unmet = unmetPasswordRules(pw);
  if (unmet.length === 0) return '';
  // Lowercase only each label's first letter so the list reads as one sentence.
  return `Password needs: ${unmet.map((l) => l.charAt(0).toLowerCase() + l.slice(1)).join(', ')}`;
};
